import { AxiosError, isAxiosError } from "axios";
import { useNotificationStore } from "@/store/notificationStore";
import $api from "@/api";

export type ErrorType = AxiosError | string;

export function toErrorType(error: unknown): ErrorType {
  if (typeof error === "string" || isAxiosError(error)) {
    return error;
  }

  return error instanceof Error ? error.message : "Unknown error";
}

export interface ApiError {
  error: string;
}

export function getErrorText(error: ErrorType): string {
  if (typeof error === "string") {
    return error;
  }

  if ("isAxiosError" in error && error.isAxiosError && error.response) {
    const data = error.response.data as ApiError | undefined;
    if (data && typeof data.error === "string") {
      return data.error;
    }
    return error.message;
  }

  return "Unknown error";
}

export type NotificationType = "success" | "error";

export interface AlertOptions {
  error?: ErrorType;
  text?: string;
  type?: NotificationType;
}

export function showNotification({ error, text, type }: AlertOptions): void {
  const content = error ? getErrorText(error) : text || "";
  const notifType: NotificationType = error ? "error" : type || "success";
  useNotificationStore.getState().addNotification(content, notifType);
}

/**
 * Convert a plain object into FormData.
 * - Files and FileLists are appended as files
 * - Arrays are appended with repeated keys
 * - Booleans & numbers are stringified
 * - null/undefined fields are skipped
 */
export function toFormData<T extends Record<string, any>>(data: T): FormData {
  const form = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value == null) return;

    if (value instanceof File) {
      form.append(key, value);
      return;
    }

    if (value instanceof FileList) {
      Array.from(value).forEach((file) => form.append(key, file));
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item instanceof File) {
          form.append(key, item);
        } else if (item != null) {
          form.append(key, String(item));
        }
      });
      return;
    }

    form.append(key, String(value));
  });

  return form;
}

export async function postForm<Data, Response>(
  url: string,
  data: Data
): Promise<Response> {
  const formData = toFormData(data as any);
  const res = await $api.post<Response>(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}
